import React, { Fragment, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Form, Modal, Button, Nav, Tab, Spinner } from "react-bootstrap";
import * as Api from "../../General/api";
import * as Common from "../../General/common";
import { apiUrls } from "../../constants/endpoints";
import Description from '@material-ui/icons/Description';
import DataTable from "react-data-table-component";
import ElectronicAOBSigning from "./ElectronicAOBSigning";


const filterResidentsByAob = (medBResidents) => {
  if (!Array.isArray(medBResidents) || medBResidents.length === 0) {
    return { residentsWithAOB: [], residentsWithoutAOB: [] };
  }

  const hasAobUrl = (resident) => {
    const url = resident.aobPdfUrl ?? "";
    return typeof url === "string" ? url.trim() !== "" : Boolean(url);
  };

  const residentsWithAOB = medBResidents.filter(hasAobUrl);
  const residentsWithoutAOB = medBResidents.filter((r) => !hasAobUrl(r));

  return { residentsWithAOB, residentsWithoutAOB };
};

const aobTableCustomStyles = {
  headCells: {
    style: {
      fontWeight: "bold",
      justifyContent: "flex-start",
      backgroundColor: "#fff",
    },
  },
  cells: {
    style: {
      justifyContent: "flex-start",
    },
  },
  table: {
    style: { minWidth: "100%" },
  },
  pagination: {
    style: {
      marginTop: "0.35rem",
      marginBottom: "0.35rem",
    },
  },
};

const baseColumnConfig = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "doB", label: "DOB", format: (value) => value ? Common.GetDateString(value).replace(/-/g, "/") : "N/A" },
  { key: "unitRoom", label: "Unit/Room", format: (_, row) => `${Common.replaceNullWithString(row.unit)}/${Common.replaceNullWithString(row.room)}` }
];
const AOB_BASE_COLUMNS = baseColumnConfig.map(({ key, label, format }) => ({
  name: label,
  selector: (row) =>
    format
      ? format(row[key], row)
      : Common.replaceNullWithString(row[key]),
  sortable: true,
  wrap: false
}));

const AOBManagement = (props) => {
  const [tableScrollHeight, setTableScrollHeight] = useState("200px");
  const [selectedResidents, setSelectedResidents] = useState([]);
  const [filterWithoutAob, setFilterWithoutAob] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEmbeddedAOBForm, setShowEmbeddedAOBForm] = useState(false);

  const residentList = props.residentList ?? [];
  const medBResidents = residentList.filter((r) => r.isMedB === true || (r.medicare != null && r.medicare.trim() !== ""));
  const { residentsWithAOB, residentsWithoutAOB } = filterResidentsByAob(medBResidents);
  const listForCurrentTab = filterWithoutAob ? residentsWithoutAOB : residentsWithAOB;

  const hasCurrentTabRows = listForCurrentTab.length > 0;

  const ROW_HEIGHT = 56;
  const maxRowsPerPage = 10;
  
  const maxRowsAcrossTabs = Math.max(
    residentsWithAOB.length,
    residentsWithoutAOB.length,
    1
  );
  
  const capPx = Number.parseInt(tableScrollHeight, 10); // 200 or 300
  const desiredPx = Math.min(maxRowsAcrossTabs * ROW_HEIGHT, capPx);
  const sharedTableBodyHeight = `${desiredPx}px`;


  const isAobModalOpen = props.open && !showEmbeddedAOBForm;

  useEffect(() => {
    const updateTableHeight = () => {
      setTableScrollHeight(window.innerWidth > 1200 ? "300px" : "200px");
    };
  
    updateTableHeight(); // initial
    window.addEventListener("resize", updateTableHeight);
  
    return () => {
      window.removeEventListener("resize", updateTableHeight);
    };
  }, []);

  useEffect(() => {
    if (!isAobModalOpen) return;
    const className = "aob-management-modal-open";
    document.documentElement.classList.add(className);
    document.body.classList.add(className);

    const handleKeyDown = (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const modalEl = document.querySelector(".aob-management-modal");
      if (!modalEl) return;
      e.preventDefault();
      e.stopPropagation();
      const modal = modalEl.closest(".modal");
      // Scroll the data table body - RDT applies overflow-y:scroll to .rdt_TableBody (or its wrapper)
      let scrollEl = null;
      const tableBody = modal?.querySelector(".aob-management-datatable .rdt_TableBody");
      if (tableBody) {
        let el = tableBody;
        while (el && el.closest(".aob-management-datatable")) {
          if (el.scrollHeight > el.clientHeight) {
            scrollEl = el;
            break;
          }
          el = el.parentElement;
        }
      }
      if (!scrollEl) scrollEl = modal?.querySelector(".modal-body");
      if (scrollEl) {
        const step = 40;
        const newScrollTop = scrollEl.scrollTop + (e.key === "ArrowDown" ? step : -step);
        scrollEl.scrollTop = Math.max(0, Math.min(newScrollTop, scrollEl.scrollHeight - scrollEl.clientHeight));
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.documentElement.classList.remove(className);
      document.body.classList.remove(className);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [isAobModalOpen]);

  const handleCheckboxChange = useCallback((resident, isChecked) => {
    setSelectedResidents((prev) => {
      if (isChecked) {
        if (prev.some((r) => r.id === resident.id)) return prev; // dedupe
        return [...prev, resident];
      }
      return prev.filter((r) => r.id !== resident.id);
    });
  }, []);

  const isResidentSelected = useCallback(
    (residentId) => selectedResidents.some((r) => r.id === residentId),
    [selectedResidents]
  );

  const handleSelectAllClick = useCallback(() => {
    if (!filterWithoutAob) return;
    const list = residentsWithoutAOB;
    if (selectedResidents.length === list.length && list.length > 0) {
      setSelectedResidents([]);
    } else {
      setSelectedResidents([...list]);
    }
  }, [filterWithoutAob, residentsWithoutAOB, selectedResidents.length]);

  const handleDownloadAOB = async () => {
    if (selectedResidents.length === 0) {
      Common.showSuccessAlert(
        "Please select at least one resident",
        "warning",
        "No Selection"
      );
      return;
    }

    try {
      setIsGenerating(true);

      const payload = {
        residentIds: selectedResidents.map((r) => r.id),
        facilityName: props.facilityName || "",
      };

      const response = await Api.postRequest(apiUrls.downloadBlankAobForms, payload, false);
      const { success, message, result } = response.data;
      const pdfs = result?.pdfs || [];

      const BATCH_SIZE = 5;
      const DELAY_BETWEEN_DOWNLOADS_MS = 150;
      const DELAY_BETWEEN_BATCHES_MS = 500;

      const triggerDownload = (pdf) => {
        const byteChars = atob(pdf.pdfBase64);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteArray[i] = byteChars.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = pdf.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      };

      for (let i = 0; i < pdfs.length; i++) {
        triggerDownload(pdfs[i]);
        const isLastInBatch = (i + 1) % BATCH_SIZE === 0;
        const isLastPdf = i === pdfs.length - 1;
        if (isLastInBatch && !isLastPdf) {
          await new Promise((r) => setTimeout(r, DELAY_BETWEEN_BATCHES_MS));
        } else if (!isLastPdf) {
          await new Promise((r) => setTimeout(r, DELAY_BETWEEN_DOWNLOADS_MS));
        }
      }

      const hasFailed = result?.failedCount > 0;
      if (success) {
        
        Common.showSuccessAlertAutoClose(
          message,
          hasFailed ? "warning" : "success",
          hasFailed ? "Partial Success!" : "Success!",
          4000
        );
        setSelectedResidents([]);
      } else {
        Common.showSuccessAlert(message || "Failed to generate AOB forms", "error", "Error!");
      }
    } catch (error) {
      console.error("Error downloading blank AOB forms:", error);
      Common.showSuccessAlert(
        "An error occurred while generating AOB forms",
        "error",
        "Error!"
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadFile = useCallback(async(url, fileName, residentName) => {
    if (!url || typeof url !== "string" || !url.trim()) {
      Common.showSuccessAlert(
        "AOB PDF is not available for this resident",
        "warning",
        "No PDF"
      );
      return;
    }
  
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        const resolvedName = fileName?.trim() ||  `AOB_form_${residentName}.pdf`;
        a.download = resolvedName;
        a.click();
        debugger;


      
        URL.revokeObjectURL(blobUrl);


    } catch (error) {
      Common.showSuccessAlert(
        "Unable to download the PDF. Please try again.",
        "error",
        "Download Failed"
      );
    }
  }, []);

  const isAllSelected = listForCurrentTab.length > 0 &&
    selectedResidents.length === listForCurrentTab.length;

  const columns = useMemo(() => {
    const selectColumn = {
    name: (
        <span className="aob-select-header d-flex align-items-center">
          <span className="aob-select-label">All</span>
          <input
            type="checkbox"
            name="selectAll"
            checked={isAllSelected}
            onChange={handleSelectAllClick}
            style={{ width: 18, height: 18, cursor: "pointer" }}
            aria-label="Select all residents"
          />
        </span>
      ),
      cell: (row) => (
        <input
          type="checkbox"
          checked={isResidentSelected(row.id)}
          onChange={(e) => handleCheckboxChange(row, e.target.checked)}
          style={{ width: 18, height: 18, cursor: "pointer"}}
          aria-label={`Select ${row.firstName} ${row.lastName}`}
        />
      ),
      width: "80px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    };

    const aobColumn = {
      name: (<span className="mr-9">AOB</span>),
      cell: (row) => (
        <Description
          style={{
            color: "#20c997",
            fontSize: 28,
            cursor: row.aobPdfUrl ? "pointer" : "default",
          }}
          onClick={() => row.aobPdfUrl && downloadFile(row.aobPdfUrl, row.imageAOBName, row.firstName + " " + row.lastName)}
        />
      ),
      width: "80px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    };

    return [...AOB_BASE_COLUMNS, filterWithoutAob ? selectColumn : aobColumn];
  }, [filterWithoutAob, isResidentSelected, handleCheckboxChange, downloadFile,   listForCurrentTab.length,
    selectedResidents.length,]);

  const handleInitiateElectronicSigning = () => {
    if (selectedResidents.length === 0) {
      Common.showSuccessAlert(
        "Please select at least one resident",
        "warning",
        "No Selection",
        3000
      );
      return;
    }
    setShowEmbeddedAOBForm(true);
  };


  const handleCloseModal = () => {
    setSelectedResidents([]);
    setFilterWithoutAob(true);
    setShowEmbeddedAOBForm(false);
    props.onClose();
  };

  const handleSignedSuccess = () => {
    setSelectedResidents([]);
    setShowEmbeddedAOBForm(false);
    if (props.onRefresh) props.onRefresh();
  };

  const onTabSelect = (k) => {
    setFilterWithoutAob(k === "true");
    setSelectedResidents([]);
    setShowEmbeddedAOBForm(false);
  };

  return (
    <Fragment>
      <Modal
        size="lg"
        centered
        scrollable
        backdrop="static"
        keyboard={false}
        show={props.open && !showEmbeddedAOBForm}
        onHide={handleCloseModal}
        dialogClassName="aob-management-modal-dialog"
        contentClassName="aob-management-modal"
      >
        <Modal.Header className="py-2 position-relative">
          <div className="w-100 pr-4">
            <span className="align-middle" style={{ fontSize: "1.2rem", lineHeight: "1.1" }}>AOB Management</span>
            <div className="mt-1" style={{ fontSize: "0.85rem", lineHeight: "1.35" }}>
              <div>View and manage Assignment of Benefits (AOB) documents for Medicare residents.</div>
              <div className=" mt-1">Select resident(s) without an AOB on file to download or electronically sign AOBs.</div>
              <div>One signature may be applied to multiple residents.</div>
            </div>
          </div>
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={handleCloseModal}
            style={{ position: "absolute", top: "16px", right: "12px" }}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </Modal.Header>

        <Modal.Body className="py-2">


          <Form>
            <Tab.Container activeKey={filterWithoutAob ? "true" : "false"} onSelect={onTabSelect}>

              <Nav variant="pills" className="d-flex justify-content-center my-2 mb-5" style={{ gap: "8px" }}>
                <Nav.Item>
                  <Nav.Link eventKey="true" className="aob-pill-without-aob">
                    Without AOB
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="false" className="aob-pill-with-aob">
                    With AOB
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                <hr className="my-0" />
                <div className="aob-management-datatable react-dataTable"
                     style = {{"--aob-table-body-height" : sharedTableBodyHeight}}
                >
                    <DataTable
                      columns={columns}
                      data={listForCurrentTab}
                      customStyles={aobTableCustomStyles}
                      highlightOnHover
                      striped
                      responsive
                      dense
                      pagination
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 25, 50]}
                      fixedHeader
                      fixedHeaderScrollHeight={sharedTableBodyHeight}
                      persistTableHead
                      noDataComponent={<div className="no-data-component" > No residents found.</div>}
                    />
                    {!hasCurrentTabRows && <div className="aob-pagination-spacer" />}
                </div>
              </Tab.Content>
            </Tab.Container>
          </Form>
        </Modal.Body>

        <Modal.Footer className="py-2" style={{ justifyContent: "flex-end" }}>
          {filterWithoutAob && (
            <>
              <Button
                variant="outline-secondary"
                className="btn-sm"
                onClick={handleDownloadAOB}
                disabled={selectedResidents.length === 0 || isGenerating}
                style={{ marginRight: "0.5rem" }}
              >
                {isGenerating ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="mr-1" />
                    <span className="align-middle"> Generating...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-download mr-1"></i>
                    <span className="align-middle"> Download AOB(s)</span>
                  </>
                )}
              </Button>
              <Button
                variant="primary"
                className="btn-sm"
                onClick={handleInitiateElectronicSigning}
                disabled={selectedResidents.length === 0}
                style={{ marginRight: "0.5rem" }}
              >
                <span className="align-middle">Initiate Electronic Signing</span>
              </Button>
            </>
          )}
          <Button variant="danger" className="btn-sm" onClick={handleCloseModal}>
            <span className="align-middle">Close</span>
          </Button>
        </Modal.Footer>
      </Modal>

      <ElectronicAOBSigning
        show={props.open && showEmbeddedAOBForm}
        facilityName={props.facilityName}
        selectedResidents={selectedResidents}
        onBack={() => setShowEmbeddedAOBForm(false)}
        onSignedSuccess={handleSignedSuccess}
        onClose={handleCloseModal}
      />
    </Fragment>
  );
};

export default AOBManagement;
